import React, { useEffect, useState } from 'react';
import {
  Upload,
  Image as ImageIcon,
  Video,
  Link as LinkIcon,
  Sparkles,
  Calendar,
  Trash2,
  Loader2,
  Check,
  X,
  Wand2,
  Edit3,
  Save,
  RefreshCw,
  Instagram,
  Clock,
  Hash,
  Eye,
  Database,
  Brain,
  CalendarClock,
  ChevronRight,
  BarChart3,
  ShieldCheck,
  MapPin,
} from 'lucide-react';
import ApprovalStep from '@/components/zera/ApprovalStep';
import InstagramConnect from '@/components/zera/InstagramConnect';
import PostEditor from '@/components/zera/PostEditor';
import ReadyToExport from '@/components/zera/ReadyToExport';
import FocusCityPicker from '@/components/zera/FocusCityPicker';
import {
  BrandAsset,
  BrandVoiceProfile,
  ScheduledPost,
  GeneratedWeek,
  BrainKnowledge,
  buildBrainKnowledge,
  listAssets,
  uploadAssetFile,
  createAsset,
  deleteAsset,
  listVoices,
  analyzeAssets,
  deleteVoice,
  generateWeek,
  listScheduledPosts,
  listWeeks,
  updateScheduledPost,
  deleteScheduledPost,
  deleteWeek,
  generatePostImage,
  regeneratePost,
  submitForApproval,
} from '@/lib/brandStudio';
import { getAdminRealm } from '@/lib/adminAuth';
import { brandForRealm } from '@/lib/adminBrand';



type Step = 1 | 2 | 3 | 4 | 5 | 6;

const BrandStudio: React.FC = () => {
  const [step, setStep] = useState<Step>(1);

  // Step 1
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  // URL add form
  const [urlForm, setUrlForm] = useState({
    asset_type: 'social_profile' as BrandAsset['asset_type'],
    url: '',
    title: '',
    source_platform: 'instagram',
    notes: '',
  });

  // Voices
  const [voices, setVoices] = useState<BrandVoiceProfile[]>([]);
  const [activeVoiceId, setActiveVoiceId] = useState<string | null>(null);

  // Step 2
  const [generatingWeek, setGeneratingWeek] = useState(false);

  // Auto image generation status shown over Step 3 while images paint in.
  const [imagingTotal, setImagingTotal] = useState(0);
  const [imagingDone, setImagingDone] = useState(0);
  // Result banner after a batch run: how many succeeded/failed + a message.
  const [imagingResult, setImagingResult] = useState<{
    ok: number;
    failed: number;
    message: string;
  } | null>(null);

  // The week that was just generated. Step 3 filters to ONLY this week so a
  // fresh batch always shows exactly its 7 posts (never mixed with older runs).
  const [activeWeekId, setActiveWeekId] = useState<string | null>(null);


  // Step 3 / 5 — posts + weeks
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [weeks, setWeeks] = useState<GeneratedWeek[]>([]);

  // ---- Focus City (optional) — set in Step 2, also used by Step 3 ----
  // Shown for the travel realms (/albaniaadmin, /romaniaadmin). Persisted per
  // realm in localStorage so the choice survives refreshes. Blank = AI picks.
  const realmKey = getAdminRealm();
  const showFocusCity = realmKey === 'albania' || realmKey === 'romania';
  const step2Key = `zera_focus_city_step2_${realmKey}`;
  const readLS = (k: string) => {
    try {
      return localStorage.getItem(k) || '';
    } catch {
      return '';
    }
  };
  const [weekFocusCity, setWeekFocusCityState] = useState<string>(() => readLS(step2Key));
  const persist = (k: string, v: string) => {
    try {
      if (v.trim()) localStorage.setItem(k, v);
      else localStorage.removeItem(k);
    } catch {
      /* storage unavailable — keep in memory only */
    }
  };
  const setWeekFocusCity = (v: string) => {
    setWeekFocusCityState(v);
    persist(step2Key, v);
  };
  // Step 3 images / Recreate / Rewrite Week use the Step 2 focus city.
  const step3City = showFocusCity ? weekFocusCity.trim() : '';

  // "Rewrite Week" (Step 3) progress + result.
  const [refocusTotal, setRefocusTotal] = useState(0);
  const [refocusDone, setRefocusDone] = useState(0);
  const [refocusResult, setRefocusResult] = useState<{ ok: number; failed: number; message: string } | null>(null);
  const busyStep3 = imagingTotal > 0 || refocusTotal > 0;



  // ---- Loaders ----
  const refreshAll = async () => {
    const [a, v, p, w] = await Promise.all([listAssets(), listVoices(), listScheduledPosts(), listWeeks()]);
    setAssets(a);
    setVoices(v);
    if (!activeVoiceId && v.length > 0) setActiveVoiceId(v[0].id);
    setPosts(p);
    setWeeks(w);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const activeVoice = voices.find((v) => v.id === activeVoiceId) || voices[0] || null;

  // ---- Step 1: Asset upload + analyze ----
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const result = await uploadAssetFile(file);
      if (result) {
        const type: BrandAsset['asset_type'] = file.type.startsWith('video') ? 'video' : 'image';
        await createAsset({
          asset_type: type,
          url: result.url,
          storage_path: result.path,
          title: file.name,
        });
      }
    }
    await refreshAll();
    setUploading(false);
  };

  const handleAddUrl = async () => {
    if (!urlForm.url.trim()) return;
    await createAsset({
      asset_type: urlForm.asset_type,
      url: urlForm.url.trim(),
      title: urlForm.title.trim() || urlForm.url.trim(),
      source_platform: urlForm.source_platform,
      notes: urlForm.notes.trim(),
    });
    setUrlForm({ ...urlForm, url: '', title: '', notes: '' });
    await refreshAll();
  };

  const toggleAssetSelection = (id: string) => {
    const next = new Set(selectedAssetIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedAssetIds(next);
  };

  const selectAllAssets = () => {
    setSelectedAssetIds(new Set(assets.map((a) => a.id)));
  };


  const handleAnalyze = async () => {
    if (selectedAssetIds.size === 0) {
      setAnalyzeError('Select at least one asset to learn from.');
      return;
    }
    setAnalyzeError(null);
    setAnalyzing(true);
    try {
      const voice = await analyzeAssets(Array.from(selectedAssetIds), `Voice ${new Date().toLocaleDateString()}`);
      if (voice) {
        setActiveVoiceId(voice.id);
        await refreshAll();
        setStep(2);
      } else {
        setAnalyzeError('Analysis did not return a result. Please try again.');
      }
    } catch (e: any) {
      setAnalyzeError(e?.message || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ---- Step 2: Generate week ----
  // 7 captioned posts are written first; images are painted in Step 3. When a
  // Step 2 FOCUS CITY is set, the whole week is anchored to that city and the
  // generator receives the full brain (see buildBrainKnowledge).
  const handleGenerateWeek = async () => {
    if (!activeVoice) {
      alert('Build a brand voice first (Step 1).');
      return;
    }
    const city = showFocusCity ? weekFocusCity.trim() : '';
    setGeneratingWeek(true);
    const result = await generateWeek(activeVoice, undefined, city || null);
    setGeneratingWeek(false);

    if (!result || result.posts.length === 0) {
      alert('Week generation failed. Try again.');
      return;
    }

    // Step 3 automatically uses the same Step 2 focus city (single source).


    // Lock Step 3 onto exactly this new week. Images are NOT painted here — the
    // user generates them with the button in Step 3.
    setActiveWeekId(result.week.id);
    await refreshAll();
    setStep(3);
  };

  // ---- Step 3: generate one AI image for every post in a week (button-driven) ----
  // Runs SEQUENTIALLY (one request at a time) for reliability. Each post gets
  // one automatic retry. With a Step 3 focus city, the brain is built ONCE and
  // reused for every image in the batch.
  const handleGenerateWeekImages = async (weekId: string | null, onlyMissing = true) => {
    const voice = activeVoice;
    let target = posts.filter((p) => (weekId ? p.week_id === weekId : true));
    if (onlyMissing) {
      const stillMissing = target.filter((p) => !p.image_url);
      if (stillMissing.length > 0) target = stillMissing;
    }
    if (target.length === 0) return;

    setImagingResult(null);
    setRefocusResult(null);
    setImagingTotal(target.length);
    setImagingDone(0);

    const city = step3City || null;
    let brain: BrainKnowledge | null = null;
    if (city) {
      try {
        brain = await buildBrainKnowledge(city);
      } catch {
        brain = null;
      }
    }

    let ok = 0;
    let failed = 0;
    for (const p of target) {
      let success = false;
      for (let attempt = 0; attempt < 2 && !success; attempt++) {
        try {
          await generatePostImage(p, voice, city, brain);
          success = true;
        } catch {
          if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
        }
      }
      if (success) ok++;
      else failed++;
      setImagingDone((d) => d + 1);
      await refreshAll();
    }

    setImagingTotal(0);
    setImagingDone(0);
    setImagingResult({
      ok,
      failed,
      message:
        failed === 0
          ? `All ${ok} images generated successfully${city ? ` — set in ${city}` : ''}.`
          : `${ok} image${ok === 1 ? '' : 's'} generated, ${failed} couldn't be created — the AI image service is busy or temporarily unreachable. Click "Retry Failed Images".`,
    });
  };

  // ---- Step 3: Rewrite the whole week around the Step 3 focus city ----
  // Sequentially recreates every editable post in the week (fresh caption,
  // hashtags, CTA, theme AND image) using the full brain + focus city.
  const handleRefocusWeek = async (weekId: string | null) => {
    const city = step3City;
    if (!city) return;
    const target = posts.filter(
      (p) => (weekId ? p.week_id === weekId : true) && p.approval_status !== 'pending' && p.approval_status !== 'approved',
    );
    if (target.length === 0) {
      setRefocusResult({ ok: 0, failed: 0, message: 'There are no editable posts in this week to rewrite.' });
      return;
    }
    setImagingResult(null);
    setRefocusResult(null);
    setRefocusTotal(target.length);
    setRefocusDone(0);

    let ok = 0;
    let failed = 0;
    for (const p of target) {
      let success = false;
      for (let attempt = 0; attempt < 2 && !success; attempt++) {
        try {
          await regeneratePost(p, activeVoice, city);
          success = true;
        } catch {
          if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
        }
      }
      if (success) ok++;
      else failed++;
      setRefocusDone((d) => d + 1);
      await refreshAll();
    }

    setRefocusTotal(0);
    setRefocusDone(0);
    setRefocusResult({
      ok,
      failed,
      message:
        failed === 0
          ? `All ${ok} posts rewritten around ${city} using everything the brain has learned.`
          : `${ok} post${ok === 1 ? '' : 's'} rewritten around ${city}; ${failed} couldn't be updated — try Rewrite Week again.`,
    });
  };

  // ---- Step 3: Approve / delete ----

  const handleApprovePost = async (p: ScheduledPost) => {
    await updateScheduledPost(p.id, { status: 'approved' });
    await refreshAll();
  };

  // NOTE: No window.confirm() here — native confirm dialogs are silently
  // blocked by many mobile browsers (iOS standalone / in-app webviews), which
  // made deletion impossible on phones. The Post Editor now shows an inline
  // two-tap "Confirm delete" button instead, then calls this directly.
  const handleDeletePost = async (id: string) => {
    await deleteScheduledPost(id);
    await refreshAll();
  };

  // Generate a brand-aware image for a post (honours the Step 3 focus city).
  const handleGeneratePostImage = async (p: ScheduledPost) => {
    await generatePostImage(p, activeVoice, step3City || null);
    await refreshAll();
  };

  // Fully recreate a post (caption + hashtags + CTA + theme + image), built
  // around the Step 3 focus city + full brain when one is set.
  const handleRegeneratePost = async (p: ScheduledPost): Promise<Partial<ScheduledPost>> => {
    const patch = await regeneratePost(p, activeVoice, step3City || null);
    await refreshAll();
    return patch;
  };





  const fmtDateTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // ====== UI ======

  return (
    <div className="bg-white">
      {/* Step Stepper Header */}
      <div className="bg-[#0D1B2A] px-6 py-5 border-b border-[#C9A961]/30">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
              <Brain className="w-5 h-5 text-[#C9A961]" />
            </div>
            <div>
              <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
                AI Brand Learning Studio
              </div>
              <h2 className="font-serif text-xl text-white font-light">Six-Step Social Posting Engine</h2>
            </div>
          </div>
          <button
            onClick={refreshAll}
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A] transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[
            { n: 1, label: 'Learn from Assets', icon: Database, desc: 'Upload + analyze' },
            { n: 2, label: 'Generate Week', icon: Wand2, desc: 'AI-write 7 posts' },
            { n: 3, label: 'Post Editor', icon: Instagram, desc: 'Edit like Instagram' },
            { n: 4, label: 'Admin Approval', icon: ShieldCheck, desc: 'Approve or send back' },
            { n: 5, label: 'Ready Library', icon: Eye, desc: 'Download + copy' },
            { n: 6, label: 'Analytics & Connect', icon: BarChart3, desc: 'Instagram + insights' },
          ].map(({ n, label, icon: Icon, desc }) => {
            const active = step === n;
            const done = step > n;
            return (
              <button
                key={n}
                onClick={() => setStep(n as Step)}
                className={`group text-left p-4 border transition-all ${
                  active
                    ? 'bg-[#C9A961] border-[#C9A961] text-[#0D1B2A]'
                    : done
                    ? 'bg-[#C9A961]/10 border-[#C9A961]/40 text-white'
                    : 'bg-transparent border-white/15 text-white/70 hover:border-[#C9A961]/40'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-serif text-sm ${
                        active
                          ? 'bg-[#0D1B2A] text-[#C9A961]'
                          : done
                          ? 'bg-[#C9A961] text-[#0D1B2A]'
                          : 'bg-white/10 text-white/60'
                      }`}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : n}
                    </div>
                    <Icon className="w-4 h-4" />
                  </div>
                  <ChevronRight className={`w-4 h-4 ${active ? 'text-[#0D1B2A]' : 'text-white/30'}`} />
                </div>
                <div className="font-sans text-[11px] tracking-[0.2em] uppercase font-medium">{label}</div>
                <div className={`font-sans text-[11px] mt-1 ${active ? 'text-[#0D1B2A]/70' : 'text-white/50'}`}>
                  {desc}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="p-6">
        {step === 1 && analyzeError && (
          <div className="mb-5 flex items-start gap-3 border border-red-300 bg-red-50 px-4 py-3">
            <X className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
            <div>
              <div className="font-sans text-xs font-semibold text-red-700 uppercase tracking-wider">Analysis failed</div>
              <div className="font-sans text-xs text-red-600 mt-1">{analyzeError}</div>
            </div>
          </div>
        )}
        {step === 1 && (
          <Step1Assets
            assets={assets}
            selectedAssetIds={selectedAssetIds}
            toggleAssetSelection={toggleAssetSelection}
            selectAllAssets={selectAllAssets}

            handleFileUpload={handleFileUpload}
            uploading={uploading}
            urlForm={urlForm}
            setUrlForm={setUrlForm}
            handleAddUrl={handleAddUrl}
            handleAnalyze={handleAnalyze}
            analyzing={analyzing}
            onDelete={async (id) => {
              await deleteAsset(id);
              await refreshAll();
            }}
            voices={voices}
            activeVoiceId={activeVoiceId}
            setActiveVoiceId={setActiveVoiceId}
            onDeleteVoice={async (id) => {
              if (!confirm('Delete this brand voice?')) return;
              await deleteVoice(id);
              await refreshAll();
            }}
            goToStep={(s) => setStep(s)}
          />
        )}

        {step === 2 && (
          <Step2Voice
            voice={activeVoice}
            voices={voices}
            activeVoiceId={activeVoiceId}
            setActiveVoiceId={setActiveVoiceId}
            generatingWeek={generatingWeek}
            handleGenerateWeek={handleGenerateWeek}
            goToStep={(s) => setStep(s)}
            showFocusCity={showFocusCity}
            focusCity={weekFocusCity}
            setFocusCity={setWeekFocusCity}
          />
        )}


        {step === 3 && (
          <>
            {refocusTotal > 0 && (
              <div className="mb-5 flex items-center gap-3 border border-[#C9A961]/40 bg-[#FAF8F3] px-4 py-3">
                <Loader2 className="w-4 h-4 text-[#C9A961] animate-spin flex-shrink-0" />
                <div className="font-sans text-xs text-[#0D1B2A]">
                  Rewriting this week around <strong>{step3City}</strong> with the full brain… {refocusDone}/
                  {refocusTotal} posts done (new caption + new image for each).
                </div>
              </div>
            )}
            {refocusResult && refocusTotal === 0 && (
              <div
                className={`mb-5 flex items-start gap-3 border px-4 py-3 ${
                  refocusResult.failed === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
                }`}
              >
                {refocusResult.failed === 0 ? (
                  <Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                ) : (
                  <X className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                )}
                <div className="font-sans text-xs text-[#0D1B2A] flex-1">{refocusResult.message}</div>
                <button
                  onClick={() => setRefocusResult(null)}
                  aria-label="Dismiss"
                  className="text-slate-400 hover:text-[#0D1B2A]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {imagingTotal > 0 && (
              <div className="mb-5 flex items-center gap-3 border border-[#C9A961]/40 bg-[#FAF8F3] px-4 py-3">
                <Loader2 className="w-4 h-4 text-[#C9A961] animate-spin flex-shrink-0" />
                <div className="font-sans text-xs text-[#0D1B2A]">
                  Painting AI images for your posts{step3City ? ` in ${step3City}` : ''}… {imagingDone}/{imagingTotal} done.
                  They appear below as each one finishes.
                </div>
              </div>
            )}
            {imagingResult && imagingTotal === 0 && (
              <div
                className={`mb-5 flex items-start gap-3 border px-4 py-3 ${
                  imagingResult.failed === 0
                    ? 'border-emerald-300 bg-emerald-50'
                    : 'border-amber-300 bg-amber-50'
                }`}
              >
                {imagingResult.failed === 0 ? (
                  <Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                ) : (
                  <X className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                )}
                <div className="font-sans text-xs text-[#0D1B2A]">{imagingResult.message}</div>
              </div>
            )}
            <div className="mb-5 flex items-center justify-between flex-wrap gap-3 border border-[#C9A961]/30 bg-[#FAF8F3] px-4 py-3">
              <div className="font-sans text-xs text-[#0D1B2A] max-w-xl">
                Your 7 posts are ready. Click <strong>Generate Images for This Week</strong> to paint one
                on-brand AI image for every post{step3City ? <> set in <strong>{step3City}</strong></> : null}. Images are
                created one at a time and appear below as each finishes.
                {step3City && (
                  <> Use <strong>Rewrite Week · {step3City}</strong> to rebuild every caption and image around the city.</>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {imagingResult && imagingResult.failed > 0 && imagingTotal === 0 && (
                  <button
                    onClick={() => handleGenerateWeekImages(activeWeekId, true)}
                    disabled={busyStep3}
                    className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 flex items-center gap-2 border border-[#C9A961] text-[#0D1B2A] hover:bg-[#C9A961]/10 disabled:opacity-50"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Retry Failed Images
                  </button>
                )}
                {step3City && (
                  <button
                    onClick={() => handleRefocusWeek(activeWeekId)}
                    disabled={busyStep3}
                    className={`font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 flex items-center gap-2 ${
                      busyStep3
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-[#0D1B2A] text-white hover:bg-[#1A3B5C]'
                    }`}
                  >
                    {refocusTotal > 0 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                    Rewrite Week · {step3City}
                  </button>
                )}
                <button
                  onClick={() => handleGenerateWeekImages(activeWeekId, false)}
                  disabled={busyStep3}
                  className={`font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 flex items-center gap-2 ${
                    busyStep3
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]'
                  }`}
                >
                  {imagingTotal > 0 ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="w-3.5 h-3.5" />
                  )}
                  Generate Images for This Week
                </button>
              </div>
            </div>


            <PostEditor
              posts={posts}
              weeks={weeks}
              defaultWeekId={activeWeekId}
              voice={activeVoice}


              onSave={async (id, patch) => {
                await updateScheduledPost(id, patch);
                await refreshAll();
              }}
              onSubmitForApproval={async (p) => {
                // Send the post to the admin (Step 4). It leaves Step 3
                // automatically because the editor hides 'pending' posts.
                await submitForApproval(p.id);
                await refreshAll();
                setStep(4);
              }}
              onDelete={handleDeletePost}
              onDeleteWeek={async (id) => {
                // Confirmation happens inline in the editor UI (two-tap),
                // because window.confirm() is blocked on many mobile browsers.
                await deleteWeek(id);
                if (activeWeekId === id) setActiveWeekId(null);
                await refreshAll();
              }}

              onGenerateImage={handleGeneratePostImage}
              onRegeneratePost={handleRegeneratePost}
              onUploadImage={async (file) => {
                const res = await uploadAssetFile(file);
                return res?.url || null;
              }}
              fmtDateTime={fmtDateTime}
              goToStep={(s) => setStep(s)}
            />
          </>
        )}


        {step === 4 && (
          <ApprovalStep
            posts={posts}
            refresh={refreshAll}
            fmtDateTime={fmtDateTime}
            goToStep={(s) => setStep(s)}
          />
        )}

        {step === 5 && (
          <ReadyToExport posts={posts} fmtDateTime={fmtDateTime} goToStep={(s) => setStep(s)} />
        )}

        {step === 6 && <InstagramConnect />}
      </div>
    </div>
  );
};

// ============ STEP 1 ============

interface Step1Props {
  assets: BrandAsset[];
  selectedAssetIds: Set<string>;
  toggleAssetSelection: (id: string) => void;
  selectAllAssets: () => void;

  handleFileUpload: (files: FileList | null) => void;
  uploading: boolean;
  urlForm: any;
  setUrlForm: (v: any) => void;
  handleAddUrl: () => void;
  handleAnalyze: () => void;
  analyzing: boolean;
  onDelete: (id: string) => Promise<void>;
  voices: BrandVoiceProfile[];
  activeVoiceId: string | null;
  setActiveVoiceId: (id: string) => void;
  onDeleteVoice: (id: string) => Promise<void>;
  goToStep: (s: Step) => void;
}

const Step1Assets: React.FC<Step1Props> = ({
  assets,
  selectedAssetIds,
  toggleAssetSelection,
  selectAllAssets,

  handleFileUpload,
  uploading,
  urlForm,
  setUrlForm,
  handleAddUrl,
  handleAnalyze,
  analyzing,
  onDelete,
  voices,
  activeVoiceId,
  setActiveVoiceId,
  onDeleteVoice,
  goToStep,
}) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 1 · Build the Learning Database</h3>
        <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
          Upload images and videos, or paste social profile/post URLs. The AI will analyze them to extract a
          unified brand voice — tone, vocabulary, themes, visual palette, signature phrases.
        </p>
      </div>

      {/* Upload area */}
      <div className="grid lg:grid-cols-2 gap-6">
        <label className="block border-2 border-dashed border-[#C9A961]/40 bg-[#FAF8F3] p-8 text-center cursor-pointer hover:border-[#C9A961] hover:bg-[#F3EFE6] transition-colors">
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files)}
            disabled={uploading}
          />
          <div className="w-14 h-14 mx-auto rounded-full bg-[#C9A961]/15 flex items-center justify-center mb-3">
            {uploading ? (
              <Loader2 className="w-6 h-6 text-[#C9A961] animate-spin" />
            ) : (
              <Upload className="w-6 h-6 text-[#C9A961]" />
            )}
          </div>
          <div className="font-serif text-lg text-[#0D1B2A]">
            {uploading ? 'Uploading…' : 'Drop images & videos'}
          </div>
          <div className="font-sans text-xs text-slate-500 mt-1">JPG, PNG, MP4, MOV — multiple OK</div>
        </label>

        {/* URL form */}
        <div className="border border-slate-200 p-6 bg-white">
          <div className="font-sans text-[11px] tracking-[0.2em] uppercase text-[#C9A961] mb-3">
            Or paste a social URL
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select
                value={urlForm.asset_type}
                onChange={(e) => setUrlForm({ ...urlForm, asset_type: e.target.value })}
                className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A]"
              >
                <option value="social_profile">Profile</option>
                <option value="social_post">Post</option>
                <option value="reference_url">Reference URL</option>
              </select>
              <select
                value={urlForm.source_platform}
                onChange={(e) => setUrlForm({ ...urlForm, source_platform: e.target.value })}
                className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A]"
              >
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
                <option value="pinterest">Pinterest</option>
                <option value="website">Website</option>
                <option value="other">Other</option>
              </select>
            </div>
            <input
              type="text"
              placeholder={brandForRealm(getAdminRealm()).igUrlPlaceholder}

              value={urlForm.url}
              onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
              className="w-full border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
            />
            <input
              type="text"
              placeholder="Title (optional)"
              value={urlForm.title}
              onChange={(e) => setUrlForm({ ...urlForm, title: e.target.value })}
              className="w-full border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
            />
            <textarea
              placeholder="Notes about what to learn from this (optional)"
              value={urlForm.notes}
              onChange={(e) => setUrlForm({ ...urlForm, notes: e.target.value })}
              className="w-full border border-slate-200 px-3 py-2 font-sans text-sm h-20 resize-none bg-white text-[#0D1B2A] placeholder:text-slate-400"
            />

            <button
              onClick={handleAddUrl}
              className="w-full font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center justify-center gap-2"
            >
              <LinkIcon className="w-3.5 h-3.5" /> Add to Database
            </button>
          </div>
        </div>
      </div>

      {/* Asset library */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h4 className="font-serif text-lg text-[#0D1B2A]">
              Asset Library{' '}
              <span className="font-sans text-sm text-slate-400 ml-2">({assets.length})</span>
            </h4>
            <p className="font-sans text-xs text-slate-500 mt-0.5">
              Select assets and click "Analyze & Learn" to build a brand voice.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={selectAllAssets}
              disabled={assets.length === 0}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 border transition-colors flex items-center gap-2 ${
                assets.length > 0
                  ? 'border-[#C9A961] text-[#0D1B2A] hover:bg-[#C9A961]/10'
                  : 'border-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Check className="w-3.5 h-3.5" /> Select All
            </button>
            <button
              onClick={handleAnalyze}
              disabled={analyzing || selectedAssetIds.size === 0}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors flex items-center gap-2 ${
                selectedAssetIds.size > 0 && !analyzing
                  ? 'bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing…
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" /> Analyze {selectedAssetIds.size} & Learn
                </>
              )}
            </button>
          </div>

        </div>

        {assets.length === 0 ? (
          <div className="border border-dashed border-slate-200 py-16 text-center bg-slate-50">
            <Database className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            <p className="font-sans text-sm text-slate-400">No assets yet. Upload or paste a URL above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {assets.map((a) => {
              const selected = selectedAssetIds.has(a.id);
              const isMedia = a.asset_type === 'image' || a.asset_type === 'video';
              return (
                <div
                  key={a.id}
                  onClick={() => toggleAssetSelection(a.id)}
                  className={`group relative border-2 cursor-pointer overflow-hidden transition-all ${
                    selected
                      ? 'border-[#C9A961] ring-2 ring-[#C9A961]/30'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="aspect-square bg-slate-100 relative">
                    {a.asset_type === 'image' ? (
                      <img src={a.url} alt={a.title || ''} className="w-full h-full object-cover" />
                    ) : a.asset_type === 'video' ? (
                      <video src={a.url} className="w-full h-full object-cover" muted />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                        <LinkIcon className="w-8 h-8 text-[#C9A961] mb-2" />
                        <div className="font-sans text-[10px] tracking-wider uppercase text-slate-500">
                          {a.source_platform || 'URL'}
                        </div>
                        <div className="font-sans text-[11px] text-slate-700 mt-1 line-clamp-2 break-all">
                          {a.title || a.url}
                        </div>
                      </div>
                    )}
                    {selected && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[#C9A961] flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(a.id);
                      }}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white font-sans text-[9px] tracking-wider uppercase rounded">
                      {a.asset_type === 'image' && <ImageIcon className="w-2.5 h-2.5 inline mr-1" />}
                      {a.asset_type === 'video' && <Video className="w-2.5 h-2.5 inline mr-1" />}
                      {a.asset_type.replace('_', ' ')}
                    </div>
                  </div>
                  {isMedia && a.title && (
                    <div className="p-2 font-sans text-[11px] text-slate-600 truncate bg-white">{a.title}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Existing voices */}
      {voices.length > 0 && (
        <div className="border-t border-slate-200 pt-8">
          <h4 className="font-serif text-lg text-[#0D1B2A] mb-3">Trained Brand Voices</h4>
          <div className="grid md:grid-cols-2 gap-3">
            {voices.map((v) => {
              const active = v.id === activeVoiceId;
              return (
                <div
                  key={v.id}
                  className={`border p-4 ${
                    active ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="font-serif text-base text-[#0D1B2A]">{v.name}</div>
                      <div className="font-sans text-[10px] tracking-wider uppercase text-slate-400 mt-0.5">
                        {new Date(v.created_at).toLocaleDateString()} · {(v.source_asset_ids || []).length} assets
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setActiveVoiceId(v.id)}
                        className={`font-sans text-[10px] tracking-wider uppercase px-2 py-1 border ${
                          active
                            ? 'bg-[#C9A961] text-white border-[#C9A961]'
                            : 'border-slate-200 text-slate-500 hover:border-[#C9A961]'
                        }`}
                      >
                        {active ? 'Active' : 'Use'}
                      </button>
                      <button
                        onClick={() => onDeleteVoice(v.id)}
                        className="w-7 h-7 flex items-center justify-center border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {v.summary && (
                    <p className="font-sans text-xs text-slate-600 line-clamp-2">{v.summary}</p>
                  )}
                </div>
              );
            })}
          </div>
          {voices.length > 0 && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => goToStep(2)}
                className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center gap-2"
              >
                Continue to Step 2 <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ STEP 2 ============

interface Step2Props {
  voice: BrandVoiceProfile | null;
  voices: BrandVoiceProfile[];
  activeVoiceId: string | null;
  setActiveVoiceId: (id: string) => void;
  generatingWeek: boolean;
  handleGenerateWeek: () => void;
  goToStep: (s: Step) => void;
  showFocusCity: boolean;
  focusCity: string;
  setFocusCity: (city: string) => void;
}

const Step2Voice: React.FC<Step2Props> = ({
  voice,
  voices,
  activeVoiceId,
  setActiveVoiceId,
  generatingWeek,
  handleGenerateWeek,
  goToStep,
  showFocusCity,
  focusCity,
  setFocusCity,
}) => {


  if (!voice) {
    return (
      <div className="text-center py-16">
        <Brain className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h3 className="font-serif text-xl text-[#0D1B2A]">No Brand Voice Yet</h3>
        <p className="font-sans text-sm text-slate-500 mt-2">
          Build a brand voice in Step 1 by analyzing reference assets.
        </p>
        <button
          onClick={() => goToStep(1)}
          className="mt-5 font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]"
        >
          Go to Step 1
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 2 · Generate a Week from the Voice</h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Using your trained brand voice, AI will write 7 days of on-brand Instagram posts — captions,
            hashtags, post types, and image prompts.
          </p>
        </div>
        {voices.length > 1 && (
          <select
            value={activeVoiceId || ''}
            onChange={(e) => setActiveVoiceId(e.target.value)}
            className="border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A]"

          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Focus City (optional) — travel realms only. Drives the week generator
          AND the Step 3 images / Recreate / Rewrite Week actions. */}
      {showFocusCity && (
        <FocusCityPicker
          stepLabel="Step 2"
          value={focusCity}
          onChange={setFocusCity}
          disabled={generatingWeek}
          description="Pick the city you want this whole week's content to focus on. The destination, hotel, restaurants, activities and culture posts — plus the images, Recreate and Rewrite Week in Step 3 — will all be built around this city."
        />
      )}

      {/* Voice profile card */}
      <div className="border border-[#C9A961]/30 bg-gradient-to-br from-[#FAF8F3] to-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#C9A961]" />
          <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
            Active Brand Voice
          </div>
        </div>
        <h4 className="font-serif text-2xl text-[#0D1B2A] mb-3">{voice.name}</h4>
        {voice.summary && (
          <p className="font-sans text-sm text-slate-700 leading-relaxed mb-5 italic border-l-2 border-[#C9A961] pl-4">
            {voice.summary}
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          <VoiceChipGroup label="Tone" items={voice.tone} accent="#1A3B5C" />
          <VoiceChipGroup label="Recurring Themes" items={voice.themes} accent="#8B6F47" />
          <VoiceChipGroup label="Signature Vocabulary" items={voice.vocabulary} accent="#C9A961" />
          <VoiceChipGroup label="Avoid" items={voice.avoid_words} accent="#b94747" />
          <VoiceChipGroup label="Visual Palette" items={voice.visual_palette} accent="#C9A961" />
          <VoiceChipGroup label="Hashtag Strategy" items={voice.hashtag_strategy} accent="#1A3B5C" />
        </div>

        {voice.visual_style && (
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-1">
              Visual Style
            </div>
            <p className="font-sans text-sm text-slate-700">{voice.visual_style}</p>
          </div>
        )}

        {voice.example_captions && voice.example_captions.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-3">
              Example On-Brand Captions
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {voice.example_captions.slice(0, 3).map((c, i) => (
                <div key={i} className="border border-slate-200 p-3 bg-white">
                  <p className="font-sans text-xs text-slate-700 leading-relaxed whitespace-pre-line line-clamp-6">
                    {c}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>


      {/* Generate controls */}
      <div className="border border-slate-200 p-6 bg-white">
        {(() => {
          const realm = getAdminRealm();
          const isTravel = realm === 'albania' || realm === 'romania';
          const country = realm === 'romania' ? 'Romania' : 'Albania';
          const city = showFocusCity ? focusCity.trim() : '';
          return (
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="max-w-2xl">
                <h4 className="font-serif text-lg text-[#0D1B2A]">Generate Next Week</h4>
                <p className="font-sans text-sm text-slate-500 mt-1">
                  Creates 7 posts (Mon–Sun) with mixed post types and varied posting times.
                </p>
                {isTravel && (
                  <div className="mt-4 border border-[#C9A961]/30 bg-[#FAF8F3] p-4">
                    <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-2">
                      Weekly Story Arc — one destination, one hotel
                    </div>
                    <ul className="font-sans text-xs text-slate-600 space-y-1">
                      <li><strong className="text-[#0D1B2A]">Monday</strong> — {city ? <>the focus city, <strong className="text-[#0D1B2A]">{city}</strong></> : <>a specific location in {country}</>}</li>
                      <li><strong className="text-[#0D1B2A]">Tuesday</strong> — a specific hotel in {city || "Monday's location"}</li>
                      <li><strong className="text-[#0D1B2A]">Wednesday</strong> — that hotel's amenities, features &amp; offerings</li>
                      <li><strong className="text-[#0D1B2A]">Thursday</strong> — local activities, restaurants &amp; events outside the hotel</li>
                      <li><strong className="text-[#0D1B2A]">Friday</strong> — deep dive into {city ? `${city}'s` : "the location's"} culture &amp; heritage</li>
                      <li><strong className="text-[#0D1B2A]">Sat + Sun</strong> — more of the city, the hotel &amp; its culture</li>
                    </ul>
                  </div>
                )}
                <div className="mt-3 inline-flex items-center gap-2 text-[#8B6F47]">
                  <ImageIcon className="w-4 h-4 text-[#C9A961]" />
                  <span className="font-sans text-sm">
                    You'll land in Step 3 with 7 posts, ready to paint one on-brand AI image for each.
                  </span>
                </div>
              </div>
              <button
                onClick={handleGenerateWeek}
                disabled={generatingWeek}
                className={`font-sans text-[10px] tracking-[0.2em] uppercase px-6 py-3 transition-colors flex items-center gap-2 ${
                  generatingWeek
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]'
                }`}
              >
                {generatingWeek ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {city ? `Building ${city} week…` : 'Generating…'}
                  </>
                ) : (
                  <>
                    <Wand2 className="w-3.5 h-3.5" /> {city ? `Generate 7 Days · ${city}` : 'Generate 7 Days'}
                  </>
                )}
              </button>

            </div>
          );
        })()}
      </div>

    </div>
  );
};

const VoiceChipGroup: React.FC<{ label: string; items: string[]; accent: string }> = ({
  label,
  items,
  accent,
}) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span
            key={`${it}-${i}`}
            className="font-sans text-[11px] px-2.5 py-1 border rounded-full bg-white"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};



export default BrandStudio;
